import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// Third Party Libraries
import DatePicker from '~/components/General/OlcDatePicker';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import {
  CLEAR_ADMIN_BATCHES_FILTERS,
  SET_ADMIN_BATCHES_FILTERS,
} from '~/redux/actions/action-types.js';
import { failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message.js';
import {
  dateProtector,
  handleRawChangeForDatePicker,
  formatDateOnly,
  parseLocalDateOnly,
} from '~/utils/date-format.js';
import { DATE_REGEX, DEFAULT_PAGINATION } from '~/utils/constants';

// Components
import MultiSelect from '~/components/General/MultiSelectNew';

// Mui components
import Typography from '@mui/material/Typography';
import { Box, Grid, InputLabel } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Styles
import './styles.scss';

const AdvanceFilters = forwardRef((props, ref) => {
  const { handleCloseFilter, open, updatePagination } = props;
  const [selectedAccuZip, setSelectedAccuZip] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
  const [createdDate, setCreatedDate] = useState([null, null]);
  const [startDate, endDate] = createdDate;

  const dispatch = useDispatch();

  const accuzipStatus = useSelector(
    (state) => state.adminBatchesReducers.filterslabels.accuZipStatus
  );

  const batchStatus = useSelector((state) => state.adminBatchesReducers.filterslabels.batchStatus);

  const productType = useSelector((state) => state.adminBatchesReducers.filterslabels.productType);

  const perPage = useSelector(
    (state) => state.adminBatchesReducers.perPage || DEFAULT_PAGINATION.pageSize
  );

  const filters = useSelector((state) => state.adminBatchesReducers.filters);

  const onClose = () => {
    clearFilters(true);
  };

  const clearFilters = (persist = false) => {
    setSelectedAccuZip([]);
    setSelectedBatch([]);
    setSelectedProduct([]);
    setCreatedDate([null, null]);
    !persist ? dispatch({ type: CLEAR_ADMIN_BATCHES_FILTERS }) : undefined;
    handleCloseFilter();
  };

  const applyFilters = () => {
    const dateRange =
      startDate && endDate
        ? {
            startDate: formatDateOnly(startDate),
            endDate: formatDateOnly(endDate),
          }
        : null;

    if (startDate && endDate === null) {
      dispatch(failure(MESSAGES.CONTACTS.END_DATE_REQUIRED));
      return;
    }

    let payload = {
      applyFilters: true,
      filters: {
        accuzipStatus: selectedAccuZip,
        batchStatus: selectedBatch,
        productType: selectedProduct,
        createdAt: dateRange,
      },
    };

    const filtersWithValues = Object.keys(payload.filters).filter((key) => {
      const value = payload.filters[key];
      return Array.isArray(value) ? value.length > 0 : value !== null;
    });

    payload.filterSize = filtersWithValues.length;

    updatePagination({
      pageSize: perPage,
      page: 1,
    });

    if (filtersWithValues.length <= 0) {
      dispatch(failure(MESSAGES.ORDERS.FILTERS_REQUIRED));
      return;
    }

    dispatch({ type: SET_ADMIN_BATCHES_FILTERS, payload: payload });
    onClose();
  };

  useImperativeHandle(ref, () => ({
    clearFilters() {
      clearFilters();
    },
  }));

  useEffect(() => {
    if (Object.values(filters).length) {
      setSelectedAccuZip(filters.accuzipStatus);
      setSelectedBatch(filters.batchStatus);
      setSelectedProduct(filters.productType);
      filters.createdAt
        ? setCreatedDate([
            parseLocalDateOnly(filters?.createdAt?.startDate),
            parseLocalDateOnly(filters?.createdAt?.endDate),
          ])
        : undefined;
    }
  }, [open]);

  return (
    <OlcFormModal
      open={open}
      onClose={onClose}
      title={MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.HEADING}
      className="batchfilterModalWrapper olcAdminFilterModal"
      contentClassName="filterModalContent"
      footerClassName="actionBtns"
      footer={
        <>
          <OlcButton variant="secondary" onClick={() => clearFilters()}>
            {MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton variant="primary" onClick={applyFilters}>
            {MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.SUBMIT_BUTTON}
          </OlcButton>
        </>
      }
    >
      <Grid container spacing={2}>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>
              {MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.ACCUZIP_STATUS_LABEL}
            </Typography>
            <MultiSelect
              options={accuzipStatus}
              selectedValue={selectedAccuZip}
              setSelectedValue={setSelectedAccuZip}
              placeHolderText={
                MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.ACCUZIP_STATUS_PLACEHOLDER
              }
              noOptionText={MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.NO_ACCUZIP_STATUS}
              clearable={true}
            />
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>
              {MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.BATCH_STATUS_LABEL}
            </Typography>
            <MultiSelect
              options={batchStatus}
              selectedValue={selectedBatch}
              setSelectedValue={setSelectedBatch}
              placeHolderText={
                MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.BATCH_STATUS_PLACEHOLDER
              }
              noOptionText={MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.NO_BATCH_STATUS}
              clearable={true}
            />
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="mailedDate">
            <Box className="selectDate">
              <InputLabel className="dateLabel">
                {MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.CREATED_DATE_LABEL}
              </InputLabel>
              <KeyboardArrowDownIcon
                sx={{
                  color: isDatepickerOpen ? '#ED5C2F' : 'inherit',
                }}
              />
              <DatePicker
                onCalendarClose={() => setIsDatepickerOpen(false)}
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => {
                  dateProtector(update);
                  setCreatedDate(update);
                }}
                minDate={null}
                onChangeRaw={(e) => {
                  if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                    handleRawChangeForDatePicker(e, setCreatedDate);
                  } else {
                    e.preventDefault();
                  }
                }}
                onFocus={() => setIsDatepickerOpen(true)}
                onBlur={(e) => {
                  if (e.target.value == '') {
                    setCreatedDate([null, null]);
                  }
                  setIsDatepickerOpen(false);
                }}
                placeholderText={MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.DATE_PLACEHOLDER}
                isClearable
              />
            </Box>
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>
              {MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.PRODUCT_TYPE_LABEL}
            </Typography>
            <MultiSelect
              options={productType}
              selectedValue={selectedProduct}
              setSelectedValue={setSelectedProduct}
              placeHolderText={
                MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.PRODUCT_TYPE_PLACEHOLDER
              }
              noOptionText={MESSAGES.ADMIN_DASHBOARD.BATCHES.ADVANCE_FILTERS.NO_PRODUCT_TYPE}
              clearable={true}
            />
          </Box>
        </Grid>
      </Grid>
    </OlcFormModal>
  );
});

export default AdvanceFilters;
